// Owned by the shared community gallery panel; imported and re-exported here so
// the store, its actions, and DEFAULT_STATE keep importing it from this module.
import { DEFAULT_COMMUNITY_GALLERY_FILTERS } from 'Pods/LazyLoaded/CommunityGalleryPanel/constants'

export { DEFAULT_COMMUNITY_GALLERY_FILTERS }

export const PROVIDER = {
  TENCENT: 'tencent',
  MESHY: 'meshy',
  HYPER3D: 'hyper3d',
  TRIPO3D: 'tripo3d',
}

export const POLY_COUNT = {
  LOW: 'low',
  MEDIUM: 'medium',
  HIGH: 'high',
}

export const FACE_LEVEL = {
  LOW: 'low',
  MEDIUM: 'medium',
  HIGH: 'high',
}

export const MODEL_FORMAT_OUTPUT = {
  GLB: 'glb',
  FBX: 'fbx',
  OBJ: 'obj',
  STL: 'stl',
  USDZ: 'usdz',
}

export const MODEL_FORMAT_EXPORT = {
  GLB: 'glb',
  FBX: 'fbx',
  OBJ: 'obj',
  STL: 'stl',
  USDZ: 'usdz',
}

export const MODE = {
  NORMAL: 'normal',
  FAST: 'fast',
}

export const GENERATE_TYPE = {
  NORMAL: 'normal',
  LOW_POLY: 'lowPoly',
  SKETCH: 'sketch',
  GEOMETRY: 'geometry',
}

export const USE_CASE = {
  STANDARD: 'standard',
  THREE_D_PRINT: 'threeDPrint',
  HERO: 'hero',
  PROP: 'prop',
  LOW_POLY: 'lowPoly',
  SHAPE_ONLY: 'shapeOnly',
}

export const VIEWER_SIDEBAR_TAB = {
  REMESH: 'remesh',
  TEXTURE: 'texture',
  SEGMENT: 'segment',
  ANIMATE: 'animate',
  RENDER: 'render',
  VARIATIONS: 'variations',
  ASSIST: 'assist',
}

// Identifies which action button started the in-flight run, so only that button
// shows the in-progress label while the others are disabled. Initial generations
// use this sentinel; enhancement runs use their VIEWER_SIDEBAR_TAB id.
export const GENERATION_TRIGGER_KEY = 'generation'

export const MOBILE_PANEL = {
  GENERATE: 'generate',
  VIEWER: 'viewer',
  ENHANCE: 'enhance',
}

export const IMAGE_ASSISTANT_TAB = {
  SEARCH: 'search',
  CREATE: 'create',
  EDIT: 'edit',
}

export const MATERIAL = {
  PBR: 'pbr',
  BASIC: 'basic',
  GEOMETRY_ONLY: 'geometry_only',
}

export const POLYGON_TYPE = {
  QUADRILATERAL: 'quadrilateral',
  TRIANGLE: 'triangle',
}

export const MODEL_ID = {
  TENCENT_TEXT_TO_3D_PRO: 'tencent-text-to-3d-pro',
  TENCENT_IMAGE_TO_3D_PRO: 'tencent-image-to-3d-pro',
  TENCENT_MESH_SIMPLIFICATION_PRO: 'tencent-mesh-simplification-pro',
  TENCENT_MESH_SEGMENTATION_PRO: 'tencent-mesh-segmentation-pro',
  TENCENT_RETEXTURE_PRO: 'tencent-retexture-pro',
  TRIPO3D_IMAGE_TO_3D_DEFAULT: 'tripo3d-image-to-3d-default',
  TRIPO3D_TEXT_TO_3D_DEFAULT: 'tripo3d-text-to-3d-default',
  TRIPO3D_RETEXTURE_DEFAULT: 'tripo3d-retexture-default',
  MESHY_TEXT_TO_3D_PRO: 'meshy-text-to-3d-pro',
  MESHY_IMAGE_TO_3D_PRO: 'meshy-image-to-3d-pro',
  MESHY_RETEXTURE_PRO: 'meshy-retexture-pro',
  MESHY_MESH_SIMPLIFICATION_PRO: 'meshy-mesh-simplification-pro',
  TRIPO3D_MESH_SIMPLIFICATION_DEFAULT: 'tripo3d-mesh-simplification-default',
  TRIPO3D_MESH_SEGMENTATION_DEFAULT: 'tripo3d-mesh-segmentation-default',
  HYPER3D_TEXT_TO_3D_GEN2: 'hyper3d-text-to-3d-gen2',
  HYPER3D_IMAGE_TO_3D_GEN2: 'hyper3d-image-to-3d-gen2',
  HYPER3D_RETEXTURE_PRO: 'hyper3d-retexture-pro',
}

export const ACTION = {
  IMAGE_TO_3D: 'image-to-3d',
  TEXT_TO_3D: 'text-to-3d',
  CONVERSION: 'conversion',
  MESH_SEGMENTATION: 'mesh-segmentation',
  MESH_SIMPLIFICATION: 'mesh-simplification',
  RETEXTURE: 'retexture',
  UV_UNFOLDING: 'uv-unfolding',
}

// Providers intentionally hidden from the AI Engine selector for a given enhanced
// action. Hidden providers are also excluded from availableEnhancedProviders so they
// can never become the auto-selected active provider while their button is not shown.
export const HIDDEN_ENHANCED_PROVIDERS_BY_ACTION = {
  [ACTION.MESH_SIMPLIFICATION]: [PROVIDER.TENCENT],
  [ACTION.MESH_SEGMENTATION]: [PROVIDER.MESHY],
}

export const LIBRARY_FILTER = {
  GENERATED_ALL: 'generated_all',
  GENERATED_FAVORITES: 'generated_favorites',
  GENERATED_DRAFTS: 'generated_drafts',
  PURCHASED: 'purchased',
  UPLOADED: 'uploaded',
}

export const VIEW_TYPE = {
  LEFT: 'left',
  RIGHT: 'right',
  BACK: 'back',
  TOP: 'top',
  BOTTOM: 'bottom',
  LEFT_FRONT: 'left_front',
  RIGHT_FRONT: 'right_front',
}

export const TEXTURE_TAB = {
  GENERATE: 'generate',
  RESOLUTION: 'resolution',
  EDIT: 'edit',
}

export const TEXTURE_ACTION_TAB = {
  IMAGE: 'image',
  TEXT: 'text',
}

export const DEFAULT_SCHEMA = {
  generationAction: ACTION.IMAGE_TO_3D,
  generationProvider: undefined,
  generationMode: MODE.NORMAL,
  generationModelSchema: {},
  availableGenerationProviders: [],
  enhancedAction: ACTION.MESH_SIMPLIFICATION,
  enhancedProvider: PROVIDER.TENCENT,
  enhancedMode: MODE.NORMAL,
  enhancedModelSchema: {},
  availableEnhancedProviders: [],
}

export const DEFAULT_MODEL_SETTINGS = {
  prompt: '',
  polyCount: POLY_COUNT.MEDIUM,
  generateType: GENERATE_TYPE.NORMAL,
  inputImage: undefined,
  inputImageUrl: undefined,
  inputThreeDUrl: undefined,
  isPrivate: false,
  selectedUseCase: USE_CASE.STANDARD,
  // default values for the standard use case
  isSmartLowPoly: false,
  material: MATERIAL.PBR,
  polygonType: POLYGON_TYPE.TRIANGLE,
  targetPolycount: 45000,
}

export const DEFAULT_UI = {
  activeViewerSidebarTab: undefined,
  activeTextureTab: TEXTURE_TAB.GENERATE,
  activeTextureActionTab: TEXTURE_ACTION_TAB.IMAGE,
  activeMobilePanel: MOBILE_PANEL.GENERATE,
  isSidebarOpen: true,
  isModelSettingsOpen: true,
  isViewerSidebarOpen: false,
  isImageAssistantOpen: false,
  imageAssistantTab: IMAGE_ASSISTANT_TAB.SEARCH,
  editImage: null,
  isAdvancedSettingsExpanded: false,
  openedModel: {},
  variationsCount: undefined,
  isModelUploading: false,
  isImageUploading: false,
  isUsageModalOpen: false,
  pendingGenerationTrigger: null,
  pendingPostAuthGenerationTrigger: null,
  isSignInModalOpen: false,
  signInModalMode: 'signup',
  signInModalStep: 'auth',
}

export const STUDIO_ACCESS_TIER = {
  NOT_SUBSCRIBED: 'not_subscribed',
  IN_FREE_BETA: 'in_free_beta',
  SUBSCRIBED: 'subscribed',
  ELIGIBLE_FOR_TRIAL: 'eligible_for_trial',
  MUST_JOIN_WAITLIST: 'must_join_waitlist',
  WAITLISTED: 'waitlisted',
  HAS_CREDITS: 'has_credits',
}

export const DEFAULT_GENERATION = {
  isGenerating: false,
  isEnhancing: false,
  triggeredBy: null,
  enhancingSessionId: undefined,
  hasGenerated: false,
  hasFailed: false,
  errorId: "",
  errorDetails: {},
  generationStartedAt: null,
  runId: undefined,
  sessionId: undefined,
  lastGenerationSettings: {},
  availableCredits: null,
}

export const DEFAULT_SUBSCRIPTION = {
  studioAccessTier: null,
  monthlyGenerationsAvailable: 0,
  monthlyGenerationsUsed: 0,
  extraGenerationsAvailable: 0,
  extraGenerationsUsed: 0,
  subscriptionPrice: null,
  subscriptionOriginalPrice: null,
  subscriptionResetDate: null,
  extraGenerationsPrice: null,
  subscriptionMonthlyGenerations: null,
  subscriptionMonthlyModels: null,
  generationCost: 0,
  subscriptionPaymentFormUrl: null,
}

export const DEFAULT_REMESH_SETTINGS = {
  isSmartLowPoly: false,
  polygonType: POLYGON_TYPE.QUADRILATERAL,
  faceLevel: FACE_LEVEL.MEDIUM,
  targetPolycount: '',
}

export const DEFAULT_TEXTURE_SETTINGS = {
  inputImage: undefined,
  inputImageUrl: undefined,
  prompt: '',
  material: MATERIAL.BASIC,
  enablePbr: false,
}

export const DEFAULT_VIEWER = {
  fileFormat: null,
  fileSize: null,
  inputThreeD: undefined,
  material: undefined,
  meshList: [],
  isRigged: false,
  explodedViewEnabled: false,
  segmentationRenderModeAvailable: false,
  selectedMeshId: null,
}

// Viewer-lifetime state kept in its own slice so per-model resets (setOpenedModel
// rebuilds the `viewer` slice from DEFAULT_VIEWER) can never wipe the live viewer
// instance the sidebar needs to drive segmentation/exploded view.
export const DEFAULT_STATIC_VIEWER = {
  instance: null,
}

export const DEFAULT_DCC_BRIDGE = {
  selectedSoftwareName: null,
  sendStatus: 'idle',
  sendingTo: null,
  isSendModalOpen: false,
  connectedSoftwares: {},
}

export const DEFAULT_EXPORT = {
  selectedFormat: MODEL_FORMAT_EXPORT.GLB,
  status: 'idle',
  activeConversion: null,
  downloadUrl: null,
}

export const DEFAULT_STATE = {
  // filters
  libraryFilter: [],
  // schema
  schema: { ...DEFAULT_SCHEMA },
  // modelSettings
  modelSettings: { ...DEFAULT_MODEL_SETTINGS },
  // textureSettings
  textureSettings: { ...DEFAULT_TEXTURE_SETTINGS },
  // generation
  generation: { ...DEFAULT_GENERATION },
  // subscription
  subscription: { ...DEFAULT_SUBSCRIPTION },
  // remeshSettings
  remeshSettings: { ...DEFAULT_REMESH_SETTINGS },
  // ui
  ui: { ...DEFAULT_UI },
  // viewer
  viewer: { ...DEFAULT_VIEWER },
  // staticViewer
  staticViewer: { ...DEFAULT_STATIC_VIEWER },
  // dccBridge
  dccBridge: { ...DEFAULT_DCC_BRIDGE },
  // export
  export: { ...DEFAULT_EXPORT },
  // communityGalleryFilters
  communityGalleryFilters: { ...DEFAULT_COMMUNITY_GALLERY_FILTERS },
}
